import {
  ApiIngredient,
  RecipeIngredient,
  isApiIngredient,
} from "types/mealPlans";

/**
 * Format an ingredient for display, handling both API and UI formats
 * Properly handles suggestedQuantity vs. regular quantity
 */
export const formatIngredient = (
  ingredient: RecipeIngredient | ApiIngredient
): string => {
  if (!ingredient) {
    return "Unknown ingredient";
  }

  try {
    if (isApiIngredient(ingredient)) {
      // Convert API format to UI format on the fly
      const { displayName, nutritionPreview } = ingredient;

      if (!nutritionPreview || !nutritionPreview.portion) {
        return displayName || "Unknown ingredient";
      }

      const { portion } = nutritionPreview;

      // Handle quantity calculation
      // If suggestedQuantity exists, use it as the displayed quantity
      // Otherwise use the original portion quantity
      let displayQuantity = portion.quantity || 0;

      if (
        portion.suggestedQuantity &&
        Array.isArray(portion.suggestedQuantity) &&
        portion.suggestedQuantity.length >= 1
      ) {
        displayQuantity = portion.suggestedQuantity[0];
      }

      // Calculate actual weight based on quantity
      const weightValue = portion.weight?.value || 0;
      const actualWeight = weightValue * displayQuantity;

      // Format quantities to 1 decimal place if not a whole number
      const formattedQuantity = displayQuantity.toFixed(
        displayQuantity % 1 === 0 ? 0 : 1
      );
      const formattedWeight = actualWeight.toFixed(
        actualWeight % 1 === 0 ? 0 : 1
      );

      return `${displayName || "Unknown ingredient"} (${formattedQuantity} ${
        portion.name || "serving"
      }, ${formattedWeight}g)`;
    } else {
      // Use our UI format directly, but also format numbers
      if (!ingredient.ingredientName) {
        return "Unknown ingredient";
      }

      const portionQuantity = ingredient.portionQuantity || 0;
      const weightGrams = ingredient.weightGrams || 0;

      const formattedQuantity = portionQuantity.toFixed(
        portionQuantity % 1 === 0 ? 0 : 1
      );
      const formattedWeight = weightGrams.toFixed(
        weightGrams % 1 === 0 ? 0 : 1
      );

      return `${ingredient.ingredientName} (${formattedQuantity} ${
        ingredient.portionSize || "serving"
      }, ${formattedWeight}g)`;
    }
  } catch (error) {
    return "Error formatting ingredient";
  }
};
